import './tap-encryption-extension';
// Imports...
import '@iotize/tap/service/impl/group';
import '@iotize/tap/service/impl/interface';

import { bufferToHexString } from '@iotize/common/byte-converter';
import { KaitaiStreamWriter } from '@iotize/common/byte-stream';
import { Encoder } from '@iotize/common/converter/api';
import { Tap, TapError } from '@iotize/tap';
import { StringConverter } from '@iotize/tap/client/impl';
import { hmacSHA256, pbkdf2 } from '@iotize/tap/crypto';
import {
  ScramLoginParams,
  ScramLoginResponseBody,
} from '@iotize/tap/service/impl/scram';

import { isCodeError } from '@iotize/common/error';
import { debug } from './debug';
import { AuthMethod, UsernamePassword } from './definitions';
import { TapAuthError } from './tap-auth-error';
import { hashLoginPassword, XOR } from './utility';

const TAG = 'Scram';

export type InputDataType = Uint8Array | string; // | LibWordArray;

export namespace ScramAuth {
  export interface ConstantKeys {
    storedKey: Uint8Array;
    serverKey: Uint8Array;
    saltedPassword: Uint8Array;
    hashedPassword: Uint8Array;
  }
  export interface Keys extends ConstantKeys {
    clientProof: Uint8Array;
    serverProof: Uint8Array;
  }

  export interface SessionData {
    // options?: ScramLoginResponseBody;
    key?: Uint8Array;
    // username?: string;
    clientNonce?: number;
    storedKey?: Uint8Array;
    serverKey?: Uint8Array;
  }
}

export function DEFAULT_SCRAM_SALT_GENERATOR(): Uint8Array {
  const data = new Array(ScramAuth.USER_SALT_SIZE).fill(0).map((_) => {
    return Math.floor(Math.random() * 0xff);
  });
  // To be 100% sure that salt will never be [0,0,0,0] which will disable login for the user
  if (data[0] === 0) {
    data[0] = 1;
  }
  return Uint8Array.from(data);
}

export class ScramAuth
  implements AuthMethod<UsernamePassword, ScramAuth.SessionData>
{
  public static CRC_LENGTH = 4;
  public static CLIENT_NONCE_SIZE = 4;
  public static SERVER_NONCE_SIZE = 4;
  public static ITERATION_NUMBER_SIZE = 4;

  // public sessionData: ScramAuth.SessionData;

  static COMMUNICATION_KEY_LABEL = 'CommunicationKey';
  // static CLIENT_PROOF_LABEL: string = "ClientProof";
  static CLIENT_KEY_LABEL = 'ClientKey';
  static SERVER_KEY_LABEL = 'ServerKey';

  static KEY_SIZE = 16;
  static CLIENT_KEY_ITERATION_NUMBER = 2;
  static SERVER_KEY_ITERATION_NUMBER = 2;
  static USER_SALT_SIZE = 4;
  static SCRAM_PASSWORD_LENGTH =
    ScramAuth.KEY_SIZE * 2 + ScramAuth.USER_SALT_SIZE;

  public nonceGenerator: () => number = (): number => {
    return Math.floor(Math.random() * 0xffffffff);
  };
  public saltGenerator: () => Uint8Array = DEFAULT_SCRAM_SALT_GENERATOR;
  public static stringConverter: Encoder<string, Uint8Array> =
    StringConverter.ascii();

  // protected _sessionState!: BehaviorSubject<SessionState>;

  // /**
  //  * Listen to session state changed
  //  */
  // public get sessionState(): Observable<SessionState> {
  //     return this._sessionState.asObservable();
  // }

  // /**
  //  * Get current session state
  //  */
  // public get sessionStateSnapshot(): SessionState {
  //     return this._sessionState.value;
  // }

  constructor(protected tap: Tap) {
    // this.sessionData = {};
  }

  // async changeCurrentUserPassword(newPassword: string): Promise<void> {
  //     let groupId = (await this.device.service.interface.getCurrentGroupId()).body()!;
  //     if (!this.sessionData.options || !groupId){
  //         throw new AuthError(AuthError.Code.NOT_LOGGED_IN, 'Cannot change password, your are not logged in');
  //     }
  //     let newPasswordKey: Uint8Array = ScramAuth.createScramPasswordKey(newPassword, this.sessionData.options);
  //     (await this.device.service.group.changePasswordKey(groupId, newPasswordKey)).successful();
  // }

  // /**
  //  * Enable/Disable scram for this tap
  //  * @param enabled
  //  */
  // async enable(enabled = true): Promise<void> {
  //     const lockInfo = (await this.device.service.interface.getSecurityOptions()).body();
  //     if (lockInfo.scramActivated === enabled) {
  //         return;
  //     }
  //     lockInfo.scramActivated = enabled;
  //     if (enabled) {
  //         lockInfo.hashPassword = true;
  //     }
  //     (await this.device.service.interface.putSecurityOptions(lockInfo)).successful();
  // }

  async changePassword(
    newPassword: string,
    groupId: number,
    salt: Uint8Array = this.saltGenerator(),
  ): Promise<void> {
    const options = {
      iterationNumber: (await this.tap.service.scram.getHashIteration()).body(),
      salt,
    };
    const newPasswordKey: Uint8Array = ScramAuth.createScramPasswordKey(
      newPassword,
      options,
    );
    debug(
      TAG,
      `Changing password key: ${bufferToHexString(
        newPasswordKey,
      )} salt=${bufferToHexString(salt)}; iteration=${
        options.iterationNumber
      } for user id "${groupId}"`,
    );
    (
      await this.tap.service.group.changePasswordKey(groupId, newPasswordKey)
    ).successful();
  }

  /**
   * Perform login
   *
   * @param params
   *
   * @throws Error if scram is not activated
   */
  public async login(params: UsernamePassword): Promise<ScramAuth.SessionData> {
    const clientNonce = this.generateNonce();
    debug(TAG, 'clientNonce', clientNonce);
    const loginParams: ScramLoginParams = {
      username: params.username,
      clientNonce,
    };
    const loginBody: ScramLoginResponseBody = (
      await this.tap.service.scram.login(loginParams)
    ).body();

    const keys = ScramAuth.computeKeys(params, loginBody, clientNonce);

    const sessionData: Partial<ScramAuth.SessionData> = {};
    sessionData.storedKey = keys.storedKey;
    sessionData.serverKey = keys.serverKey;

    const serverNonce = loginBody.serverNonce;

    const deviceServerProof: Uint8Array = (
      await this.tap.service.scram.loginProof(keys.clientProof)
    ).body();
    const expectedServerProof = keys.serverProof;

    // TODO maybe we can find something better to test array equals ...
    if (
      bufferToHexString(expectedServerProof) !==
      bufferToHexString(deviceServerProof)
    ) {
      throw new TapAuthError.InvalidServerKey(
        expectedServerProof,
        deviceServerProof,
      );
    }

    sessionData.clientNonce = clientNonce;
    sessionData.key = ScramAuth.computeSessionKey(
      clientNonce,
      serverNonce,
      loginBody.salt,
      keys.serverKey,
      keys.storedKey,
    );

    return sessionData;
  }

  public static createScramPasswordKey(
    newPassword: string,
    options: { salt: Uint8Array; iterationNumber: number },
  ): Uint8Array {
    const keys = ScramAuth.computeBaseKeys(newPassword, options);

    const saltCopy = new Uint8Array(options.salt);
    const buffer = KaitaiStreamWriter.create(ScramAuth.SCRAM_PASSWORD_LENGTH);
    buffer
      .writeBytes(keys.storedKey, ScramAuth.KEY_SIZE)
      .writeBytes(keys.serverKey, ScramAuth.KEY_SIZE)
      .writeBytes(saltCopy, ScramAuth.USER_SALT_SIZE);

    return buffer.toBytes;
  }

  public static computeBaseKeys(
    password: string,
    options: { salt: Uint8Array; iterationNumber: number },
  ): ScramAuth.ConstantKeys {
    const hashedPassword = hashLoginPassword(password);
    // debug(TAG, 'ScramAuth', 'hashedPassword', bufferToHexString(hashedPassword));
    const saltedPassword: Uint8Array = ScramAuth.saltedPassword(
      hashedPassword,
      options.salt,
      options.iterationNumber,
    );
    // debug(TAG, 'ScramAuth', 'saltedPassword', bufferToHexString(saltedPassword));
    // let clientKey: Uint8Array = ScramAuth.clientKey(saltedPassword);
    // debug(TAG, 'ScramAuth', 'clientKey', bufferToHexString(clientKey));
    const storedKey: Uint8Array = ScramAuth.storedKey(saltedPassword);
    debug(TAG, 'storeKey', bufferToHexString(storedKey));
    const serverKey: Uint8Array = ScramAuth.serverKey(saltedPassword);
    debug(TAG, 'serverKey', bufferToHexString(serverKey));
    return {
      hashedPassword,
      saltedPassword,
      storedKey,
      serverKey,
    };
  }

  public static computeKeys(
    credentials: UsernamePassword,
    loginBody: ScramLoginResponseBody,
    clientNonce: number,
  ): ScramAuth.Keys {
    const keys = ScramAuth.computeBaseKeys(credentials.password, loginBody);
    const clientProof: Uint8Array = ScramAuth.clientProof(
      keys.storedKey,
      clientNonce,
      loginBody.serverNonce,
    );
    // debug(TAG, 'ScramAuth', 'clientProof', bufferToHexString(clientProof));

    const serverProof: Uint8Array = ScramAuth.serverProof(
      keys.serverKey,
      clientNonce,
      loginBody.serverNonce,
    );
    // debug(TAG, 'ScramAuth', 'serverProof', bufferToHexString(serverProof));
    return {
      ...keys,
      clientProof,
      serverProof,
    };
  }

  public async logout(): Promise<any> {
    try {
      const response = await this.tap.service.interface.logout();
      response.successful();
      this.tap.encryption.stop();
    } catch (err) {
      if (
        isCodeError(TapError.Code.ScramNotStartedYet, err) ||
        isCodeError(TapError.Code.InvalidScramKey, err)
      ) {
        // ignore error
        this.tap.encryption.stop();
        return;
      }
      throw err;
    }
  }

  public static clientProof(
    storedKey: Uint8Array,
    clientNonce: number,
    serverNonce: number,
  ): Uint8Array {
    return ScramAuth.computeProof(storedKey, clientNonce, serverNonce).subarray(
      0,
      ScramAuth.KEY_SIZE,
    );
  }

  public static serverProof(
    serverKey: Uint8Array,
    clientNonce: number,
    serverNonce: number,
  ): Uint8Array {
    return ScramAuth.computeProof(serverKey, serverNonce, clientNonce).subarray(
      0,
      ScramAuth.KEY_SIZE,
    );
  }

  // getSessionKey(): Uint8Array {
  //     if (!this.sessionData.key) {
  //         throw new Error('Session not started');
  //     }
  //     return this.sessionData.key;
  // }

  public generateNonce() {
    return this.nonceGenerator();
  }

  /**
   * SaltedPwd = PBKDF2 ( HashedPassword, UserSalt, ItCnt )
   * @param password
   * @param userSalt
   * @param iteration
   */
  public static saltedPassword(
    hashedPassword: InputDataType,
    userSalt: InputDataType,
    iterations: number,
  ): Uint8Array {
    return pbkdf2(hashedPassword, userSalt, iterations);
  }

  /**
   * ClientKey = HMAC ( SaltedPwd | « ClientKey »)
   * @param saltedPassword
   */
  // public static clientKey(saltedPassword: InputDataType): Uint8Array{
  //     let encodedLabel = ScramAuth.encodeLabel(ScramAuth.CLIENT_KEY_LABEL);
  //     let encodedLabel2 : Uint8Array = KaitaiStreamWriter.mergeArrays(
  //         encodedLabel,
  //         Uint8Array.from([0])
  //     ).data;
  //     // debug(TAG, 'ScramAuth', 'clientKey()', 'data => ', bufferToHexString(data), 'len', data.length, `(${saltedPassword.length} + ${encodedLabel.length})`);
  //     return ScramAuth.HMAC(
  //         saltedPassword,
  //         // userSalt
  //         encodedLabel2
  //     ).subarray(0, ScramAuth.KEY_SIZE);
  // }

  /**
   * StoredKey = H ( ClientKey )
   * @param saltedPassword
   */
  public static storedKey(saltedPassword: InputDataType): Uint8Array {
    return ScramAuth.HASH(
      saltedPassword,
      ScramAuth.CLIENT_KEY_LABEL,
      ScramAuth.CLIENT_KEY_ITERATION_NUMBER,
    );
  }

  static serverKey(saltedPassword: InputDataType): Uint8Array {
    return ScramAuth.HASH(
      saltedPassword,
      ScramAuth.SERVER_KEY_LABEL,
      ScramAuth.SERVER_KEY_ITERATION_NUMBER,
    );
  }

  private static HASH(
    key: InputDataType,
    label: InputDataType,
    iteration: number,
  ) {
    return pbkdf2(key, label, iteration, ScramAuth.KEY_SIZE / 4);
  }

  /**
   * ClientSignature = HMAC ( StoredKey | ClientNonce | ServerNonce )
   * @param key
   * @param nonce1
   * @param nonce2
   */
  public static computeProof(key: Uint8Array, nonce1: number, nonce2: number) {
    const buffer = KaitaiStreamWriter.create(
      key.length + ScramAuth.CLIENT_NONCE_SIZE + ScramAuth.SERVER_NONCE_SIZE,
    );
    buffer
      .writeU(nonce1, ScramAuth.CLIENT_NONCE_SIZE)
      .writeBytes(key)
      .writeU(nonce2, ScramAuth.SERVER_NONCE_SIZE);
    return hmacSHA256(buffer.toBytes, key);
  }

  /**
   * ClientProofCheck = StoredKey ^ ClientProof
   * @param storedKey
   * @param clientProof
   */
  public static clientProofCheck(
    storedKey: Uint8Array,
    clientProof: Uint8Array,
  ) {
    return XOR(storedKey, clientProof);
  }

  /**
   * CommunicationKey = H ( ClientNonce | ServerNonce | StoredKey | « CommunicationKey » )
   * @param clientNonce
   * @param serverNonce
   * @param storedKey
   */
  public static computeSessionKey(
    clientNonce: number,
    serverNonce: number,
    userSalt: Uint8Array,
    serverKey: Uint8Array,
    storedKey: Uint8Array,
  ): Uint8Array {
    const encodedLabel = ScramAuth.encodeLabel(
      ScramAuth.COMMUNICATION_KEY_LABEL,
    );

    const buffer = KaitaiStreamWriter.create(
      ScramAuth.CLIENT_NONCE_SIZE +
        ScramAuth.SERVER_NONCE_SIZE +
        userSalt.length +
        serverKey.length +
        storedKey.length,
      // + encodedLabel.length
    );

    buffer
      .writeU(clientNonce, ScramAuth.CLIENT_NONCE_SIZE)
      .writeBytes(serverKey)
      .writeBytes(userSalt)
      .writeBytes(storedKey)
      .writeU(serverNonce, ScramAuth.SERVER_NONCE_SIZE);
    // .add(encodedLabel)

    return hmacSHA256(buffer.toBytes, serverKey).subarray(
      0,
      ScramAuth.KEY_SIZE,
    );
  }

  /**
   *
   * @param input
   */
  static encodeLabel(input: string): Uint8Array {
    return this.stringConverter.encode(input);
  }
}
